/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { PLATS, NM, COST_FIELDS, PRE_MONTHS } from '../utils/constants';
import { calcPlatform, calcRoyalties } from '../utils/calc';
import { mlSafe, fu } from '../utils/format';
import { getCostState, getDealTerms } from '../utils/stateFactory';
import supabase, { orgId } from '../supabaseClient';
import useCashSettings, { defaultCashSettings } from '../hooks/useCashSettings';

const DELAY_OPTIONS = [
  { value: 0, label: '0' },
  { value: 1, label: '30d' },
  { value: 2, label: '60d' },
  { value: 3, label: '90d' },
];

const QUARTER_STARTS = [
  { value: 0, label: 'Jan/Apr/Jul/Oct' },
  { value: 1, label: 'Feb/May/Aug/Nov' },
  { value: 2, label: 'Mar/Jun/Sep/Dec' },
];

// Apply per-month delay to a monthly revenue/cost array
// perMonthDelays: { monthIdx: delayMonths }
function applyPerMonthDelays(sourceArr, defaultDelay, perMonthDelays) {
  const out = Array(NM).fill(0);
  for (let i = 0; i < NM; i++) {
    const v = sourceArr[i] || 0;
    if (!v) continue;
    const delay = perMonthDelays?.[i] !== undefined ? parseInt(perMonthDelays[i]) : (defaultDelay ?? 1);
    const dest = i + delay;
    if (dest < NM) out[dest] += v;
    // amounts shifted beyond NM are lost (outside forecast window)
  }
  return out;
}

function calcGameRoyalties(PS, CS, DT, game, quarterly, quarterStart, platformTerms, platformMonthly) {
  const enabledPlats = PLATS.filter(p => PS[game]?.[p.key]?.enabled);

  // Cash basis: use timing-adjusted receipts, not raw revenue
  const aN = Array(NM).fill(0);
  enabledPlats.forEach(p => {
    const revUSD = Array(NM).fill(0);
    calcPlatform(p.key, PS[game][p.key]).netRaw.forEach((v, i) => { if (v !== null) revUSD[i] += v; });
    const cashReceipts = applyPerMonthDelays(revUSD, platformTerms?.[p.key] ?? 1, platformMonthly?.[p.key]);
    cashReceipts.forEach((v, i) => { aN[i] += v; });
  });

  // Cash basis: recoupable costs paid (also timing-adjusted — use as-is since already on cash basis)
  const aR = Array(NM).fill(0);
  const costs = getCostState(CS, game);
  COST_FIELDS.forEach(({ key }) => {
    costs.recoup[key].forEach((v, arrayIdx) => {
      const i = arrayIdx - PRE_MONTHS;
      if (i >= 0 && i < NM) aR[i] += parseFloat(v) || 0;
    });
  });

  const dt = getDealTerms(DT, game);
  const { royaltyPayableRaw } = calcRoyalties(aN, aR, dt);
  if (!quarterly) return royaltyPayableRaw.map(v => v || 0);

  // Aggregate to quarterly payment dates
  const q = Array(NM).fill(0);
  for (let i = 0; i < NM; i++) {
    const monthInQ = ((i - quarterStart) % 3 + 3) % 3;
    const qEnd = Math.min(i + (2 - monthInQ), NM - 1);
    q[qEnd] = (q[qEnd] || 0) + (royaltyPayableRaw[i] || 0);
  }
  return q;
}


function FormattedInput({ value, onChange, placeholder = '0', style = {} }) {
  const [focused, setFocused] = React.useState(false);
  const display = focused
    ? (value || '')
    : (parseFloat(value) > 0 ? Math.round(parseFloat(value)).toLocaleString('en-US') : (value || ''));
  return (
    <input
      type={focused ? 'number' : 'text'}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={style}
    />
  );
}

const SETTINGS_KEY = 'cash_settings';
function defaultSettings() {
  return {
    openingCash: '',
    platformTerms: {},       // { platKey: defaultDelay }
    platformMonthly: {},     // { platKey: { monthIdx: delay } }
    devAdvTerms: {},         // { gameName: defaultDelay }
    devAdvMonthly: {},       // { gameName: { monthIdx: delay } }
    pubCostsPct: { 0: '100', 1: '0', 2: '0' },
    pubCostsMonthlyPct: {},  // { monthIdx: { 0: pct, 1: pct, 2: pct } }
    royaltyTerms: {},        // { gameName: defaultDelay }
    royaltyMonthly: {},      // { gameName: { monthIdx: delay } }
    royaltyQuarterly: {},
    royaltyQuarterStart: {},
    payrollPct: '100', // % paid in current month
  };
}

// ── PER-MONTH DELAY ROW ──────────────────────────────────────────────────────
function MonthDelayRow({ label, sourceArr, defaultDelay, monthlyDelays, onChangeDefault, onChangeMonth, startDate }) {
  const hasAny = sourceArr.some(v => v > 0);
  return (
    <>
      <tr className="ri">
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Standard Terms</span>
            <select value={defaultDelay ?? 1} onChange={e => onChangeDefault(parseInt(e.target.value))}
              style={{ fontSize: 10, padding: '3px 20px 3px 6px' }}>
              {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </td>
        {Array.from({ length: NM }, (_, i) => (
          <td key={i}>
            {(sourceArr[i] || 0) > 0 ? (
              <select
                value={monthlyDelays?.[i] !== undefined ? monthlyDelays[i] : (defaultDelay ?? 1)}
                onChange={e => onChangeMonth(i, parseInt(e.target.value))}
                style={{ fontSize: 9, padding: '2px 16px 2px 4px', width: '100%' }}>
                {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : null}
          </td>
        ))}
      </tr>
    </>
  );
}


// ── COLLAPSIBLE RECEIPT/ROYALTY ROWS ─────────────────────────────────────────
function CollapsibleReceiptsRows({ platformData, summary }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <>
      <tr className="rc" style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <td>
          <span style={{ fontSize: 9, color: 'var(--blue)', marginRight: 6 }}>
            {expanded ? '▾' : '▸'}
          </span>
          Platform Receipts
        </td>
        {summary.receipts.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
      </tr>
      {expanded && platformData.map(({ key, label, paid }) => (
        <tr key={key} className="rc" style={{ background: 'var(--s0)' }}>
          <td style={{ paddingLeft: 20, fontSize: 10, color: 'var(--t3)' }}>↳ {label}</td>
          {paid.map((v, i) => <td key={i} style={{ fontSize: 10, color: 'var(--t3)' }}>{v ? fu(v) : '—'}</td>)}
        </tr>
      ))}
    </>
  );
}

function CollapsibleRoyaltiesRows({ royaltyData, summary }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <>
      <tr className="rc" style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <td>
          <span style={{ fontSize: 9, color: 'var(--blue)', marginRight: 6 }}>
            {expanded ? '▾' : '▸'}
          </span>
          Royalties
        </td>
        {summary.royOut.map((v, i) => <td key={i}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
      </tr>
      {expanded && royaltyData.map(({ game, paid }) => (
        <tr key={game} className="rc" style={{ background: 'var(--s0)' }}>
          <td style={{ paddingLeft: 20, fontSize: 10, color: 'var(--t3)' }}>↳ {game}</td>
          {paid.map((v, i) => <td key={i} style={{ fontSize: 10, color: 'var(--t3)' }}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
        </tr>
      ))}
    </>
  );
}

// useCashSettings is now in src/hooks/useCashSettings.js

export default function CashPanel({ games, PS, CS, DT, startDate, functionalCurrency, convert, peopleOhTotal = [], peopleDevTotal = [], settings: settingsProp, save: saveProp, ohCategories = [], getCategoryMonthlyTotal }) {
  const fc = functionalCurrency || 'USD';
  const { settings: internalSettings, save: internalSave } = useCashSettings();
  const settings = { ...defaultCashSettings(), ...(settingsProp || internalSettings || {}) };
  const save = saveProp || internalSave;
  const [saving, setSaving] = useState(false);

  const setPlatDefault  = (pk, v)     => save(s => ({ ...s, platformTerms:   { ...s.platformTerms,   [pk]: v } }));
  const setPlatMonth    = (pk, mi, v) => save(s => ({ ...s, platformMonthly: { ...s.platformMonthly, [pk]: { ...(s.platformMonthly[pk]||{}), [mi]: v } } }));
  const setDevDefault   = (gn, v)     => save(s => ({ ...s, devAdvTerms:     { ...s.devAdvTerms,     [gn]: v } }));
  const setDevMonth     = (gn, mi, v) => save(s => ({ ...s, devAdvMonthly:   { ...s.devAdvMonthly,   [gn]: { ...(s.devAdvMonthly[gn]||{}), [mi]: v } } }));
  const setPubPct       = (b, v)      => save(s => ({ ...s, pubCostsPct:     { ...s.pubCostsPct,     [b]:  v } }));
  const setPubMonthPct  = (mi, b, v)  => save(s => ({ ...s, pubCostsMonthlyPct: { ...s.pubCostsMonthlyPct, [mi]: { ...(s.pubCostsMonthlyPct[mi]||{}), [b]: v } } }));
  const setRoyDefault   = (gn, v)     => save(s => ({ ...s, royaltyTerms:    { ...s.royaltyTerms,    [gn]: v } }));
  const setRoyMonth     = (gn, mi, v) => save(s => ({ ...s, royaltyMonthly:  { ...s.royaltyMonthly,  [gn]: { ...(s.royaltyMonthly[gn]||{}), [mi]: v } } }));
  const setRoyQtrly     = (gn, v)     => save(s => ({ ...s, royaltyQuarterly:    { ...s.royaltyQuarterly,    [gn]: v } }));
  const setRoyQtrStart  = (gn, v)     => save(s => ({ ...s, royaltyQuarterStart: { ...s.royaltyQuarterStart, [gn]: v } }));

  // ── PLATFORM RECEIPTS
  const platformData = useMemo(() => PLATS.map(p => {
    const enabled = games.filter(g => PS[g]?.[p.key]?.enabled);
    if (!enabled.length) return null;
    const revUSD = Array(NM).fill(0);
    enabled.forEach(g => {
      calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) revUSD[i] += v; });
    });
    const revFC = revUSD.map((v, i) => convert ? convert(v, fc, i) : v);
    const paid  = applyPerMonthDelays(revFC, settings.platformTerms[p.key] ?? 1, settings.platformMonthly[p.key]);
    return { key: p.key, label: p.label, revFC, paid, totalPaid: paid.reduce((a,v)=>a+v,0) };
  }).filter(Boolean), [games, PS, settings.platformTerms, settings.platformMonthly, fc, convert]);

  // ── DEV ADVANCES
  const devAdvData = useMemo(() => games.map(g => {
    const costs = getCostState(CS, g);
    const monthly = Array(NM).fill(0);
    ['recoup','nonRecoup'].forEach(sec => {
      costs[sec].devAdvances.forEach((v, ai) => {
        const i = ai - PRE_MONTHS;
        if (i >= 0 && i < NM) monthly[i] += parseFloat(v) || 0;
      });
    });
    const paid = applyPerMonthDelays(monthly, settings.devAdvTerms[g] ?? 0, settings.devAdvMonthly[g]);
    return { game: g, monthly, paid };
  }).filter(d => d.monthly.some(v => v > 0)), [games, CS, settings.devAdvTerms, settings.devAdvMonthly]);

  // ── PUBLISHING COSTS
  const pubData = useMemo(() => {
    const byType = {};
    COST_FIELDS.filter(f => f.key !== 'devAdvances').forEach(({ key, label }) => {
      const monthly = Array(NM).fill(0);
      games.forEach(g => {
        const costs = getCostState(CS, g);
        ['recoup','nonRecoup'].forEach(sec => {
          (costs[sec][key] || []).forEach((v, ai) => {
            const i = ai - PRE_MONTHS;
            if (i >= 0 && i < NM) monthly[i] += parseFloat(v) || 0;
          });
        });
      });
      if (monthly.some(v => v > 0)) byType[key] = { label, monthly };
    });
    const total = Array(NM).fill(0);
    Object.values(byType).forEach(({ monthly }) => monthly.forEach((v, i) => { total[i] += v; }));

    // Per-month payment split
    const paid = Array(NM).fill(0);
    for (let i = 0; i < NM; i++) {
      if (!total[i]) continue;
      const overrides = settings.pubCostsMonthlyPct[i];
      const p0 = parseFloat(overrides?.[0] ?? settings.pubCostsPct[0]) / 100 || 0;
      const p1 = parseFloat(overrides?.[1] ?? settings.pubCostsPct[1]) / 100 || 0;
      const p2 = parseFloat(overrides?.[2] ?? settings.pubCostsPct[2]) / 100 || 0;
      paid[i]     += total[i] * p0;
      if (i+1 < NM) paid[i+1] += total[i] * p1;
      if (i+2 < NM) paid[i+2] += total[i] * p2;
    }
    const totalPct = (parseFloat(settings.pubCostsPct[0])||0) + (parseFloat(settings.pubCostsPct[1])||0) + (parseFloat(settings.pubCostsPct[2])||0);
    const pctWarning = totalPct > 100;
    return { byType, total, paid, pctWarning };
  }, [games, CS, settings.pubCostsPct, settings.pubCostsMonthlyPct]);

  // ── ROYALTIES
  const royaltyData = useMemo(() => games.map(g => {
    const quarterly = settings.royaltyQuarterly[g] ?? false;
    const qStart    = settings.royaltyQuarterStart[g] ?? 0;
    const monthly   = calcGameRoyalties(PS, CS, DT, g, quarterly, qStart, settings.platformTerms, settings.platformMonthly);
    const paid      = applyPerMonthDelays(monthly, settings.royaltyTerms[g] ?? 1, settings.royaltyMonthly[g]);
    return { game: g, monthly, paid, quarterly, qStart };
  }).filter(d => d.monthly.some(v => v > 0)), [games, PS, CS, DT, settings.royaltyTerms, settings.royaltyMonthly, settings.royaltyQuarterly, settings.royaltyQuarterStart]);

  // ── PAYROLL
  const payrollData = useMemo(() => {
    const totalPeople = Array(NM).fill(0);
    peopleOhTotal.forEach((v, i) => { totalPeople[i] += v || 0; });
    peopleDevTotal.forEach((v, i) => { totalPeople[i] += v || 0; });
    const pct0 = Math.min(1, Math.max(0, parseFloat(settings?.payrollPct ?? '100') || 100) / 100);
    const pct1 = Math.max(0, 1 - pct0);
    const paid = Array(NM).fill(0);
    for (let i = 0; i < NM; i++) {
      const v = totalPeople[i];
      paid[i] += v * pct0;
      if (i+1 < NM) paid[i+1] += v * pct1;
    }
    return { totalPeople, paid, pct0, pct1 };
  }, [peopleOhTotal, peopleDevTotal, settings.payrollPct]);

  // ── CASHFLOW SUMMARY
  const summary = useMemo(() => {
    const receipts = Array(NM).fill(0);
    const devOut   = Array(NM).fill(0);
    const pubOut   = [...pubData.paid];
    const royOut     = Array(NM).fill(0);
    const payrollOut = [...(payrollData?.paid || Array(NM).fill(0))];
    platformData.forEach(({ paid }) => paid.forEach((v, i) => { receipts[i]  += v; }));
    devAdvData.forEach(({ paid })   => paid.forEach((v, i) => { devOut[i]    += v; }));
    royaltyData.forEach(({ paid })  => paid.forEach((v, i) => { royOut[i]    += v; }));
    const openingCash = parseFloat(settings.openingCash) || 0;
    const net  = Array(NM).fill(0);
    const open = Array(NM).fill(0);
    const close = Array(NM).fill(0);
    // Overhead category outflows
    const ohCatOut = (ohCategories || []).map(cat => {
      const monthly = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : Array(NM).fill(0);
      return { id: cat.id, name: cat.name, monthly };
    });
    const totalOhCat = Array(NM).fill(0);
    ohCatOut.forEach(({ monthly }) => monthly.forEach((v, i) => { totalOhCat[i] += v; }));

    for (let i = 0; i < NM; i++) {
      net[i]   = receipts[i] - devOut[i] - pubOut[i] - royOut[i] - payrollOut[i] - totalOhCat[i];
      open[i]  = i === 0 ? openingCash : close[i-1];
      close[i] = open[i] + net[i];
    }
    return { receipts, devOut, pubOut, royOut, payrollOut, ohCatOut, totalOhCat, net, open, close };
  }, [platformData, devAdvData, pubData, royaltyData, payrollData, settings.openingCash, ohCategories, getCategoryMonthlyTotal]); // eslint-disable-line

  const hdr = (
    <thead>
      <tr>
        <th style={{ textAlign: 'left', minWidth: 180 }}></th>
        {Array.from({ length: NM }, (_, i) => <th key={i}>{mlSafe(i, startDate)}</th>)}
      </tr>
    </thead>
  );

  const colorVal = (v, invert = false) => {
    if (!v && v !== 0) return '—';
    const positive = invert ? v <= 0 : v >= 0;
    return <span style={{ color: positive ? 'var(--green)' : 'var(--red)' }}>{fu(v)}</span>;
  };

  return (
    <div className="panel">

      {/* PLATFORM RECEIPTS */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Platform Receipts ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            {platformData.map(({ key, label, revFC, paid }) => (
              <React.Fragment key={key}>
                <tr className="rh"><td colSpan={NM+1}>{label}</td></tr>
                <tr className="rc">
                  <td>Net Revenue</td>
                  {revFC.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
                <MonthDelayRow
                  sourceArr={revFC}
                  defaultDelay={settings.platformTerms[key] ?? 1}
                  monthlyDelays={settings.platformMonthly[key]}
                  onChangeDefault={v => setPlatDefault(key, v)}
                  onChangeMonth={(mi, v) => setPlatMonth(key, mi, v)}
                  startDate={startDate}
                />
                <tr className="rt">
                  <td>Assumed Receipt Month</td>
                  {paid.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
              </React.Fragment>
            ))}
            {!platformData.length && <tr><td colSpan={NM+1} className="nomsg">No platforms configured.</td></tr>}
          </tbody></table></div>
        </div>
      </div>

      {/* DEV ADVANCES */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Development Advances ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            {devAdvData.map(({ game, monthly, paid }) => (
              <React.Fragment key={game}>
                <tr className="rh"><td colSpan={NM+1}>{game}</td></tr>
                <tr className="rc">
                  <td>Forecast Advances</td>
                  {monthly.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
                <MonthDelayRow
                  sourceArr={monthly}
                  defaultDelay={settings.devAdvTerms[game] ?? 0}
                  monthlyDelays={settings.devAdvMonthly[game]}
                  onChangeDefault={v => setDevDefault(game, v)}
                  onChangeMonth={(mi, v) => setDevMonth(game, mi, v)}
                  startDate={startDate}
                />
                <tr className="rt">
                  <td>Assumed Payment Month</td>
                  {paid.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
              </React.Fragment>
            ))}
            {!devAdvData.length && <tr><td colSpan={NM+1} className="nomsg">No development advances entered.</td></tr>}
          </tbody></table></div>
        </div>
      </div>

      {/* PUBLISHING COSTS */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Other Publishing Costs ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            {Object.entries(pubData.byType).map(([key, { label, monthly }]) => (
              <tr key={key} className="rc">
                <td>{label}</td>
                {monthly.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
              </tr>
            ))}
            <tr className="rt">
              <td>Total Publishing Costs</td>
              {pubData.total.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
            </tr>
            <tr className="rx"><td colSpan={NM+1} /></tr>
            {/* Standard % row + per-month overrides */}
            <tr className="ri">
              <td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                  {[{b:0,lbl:'Current %'},{b:1,lbl:'30d %'},{b:2,lbl:'60d %'}].map(({b,lbl}) => (
                    <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 9, color: 'var(--t3)', minWidth: 52 }}>{lbl}</span>
                      <input type="number" min="0" max="100" className="ni" style={{ width: 52 }}
                        value={settings.pubCostsPct[b] || ''}
                        onChange={e => setPubPct(b, e.target.value)} />
                    </div>
                  ))}
                </div>
              </td>
              {Array.from({ length: NM }, (_, i) => (
                <td key={i} style={{ verticalAlign: 'top', padding: '4px 2px' }}>
                  {pubData.total[i] > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[{b:0,lbl:'0d'},{b:1,lbl:'30d'},{b:2,lbl:'60d'}].map(({b,lbl}) => (
                        <input key={b} type="number" min="0" max="100"
                          className="ci" style={{ fontSize: 9 }}
                          placeholder={settings.pubCostsPct[b] || ''}
                          value={settings.pubCostsMonthlyPct[i]?.[b] ?? ''}
                          onChange={e => setPubMonthPct(i, b, e.target.value)}
                        />
                      ))}
                    </div>
                  ) : null}
                </td>
              ))}
            </tr>
            {pubData.pctWarning && (
              <tr className="rc">
                <td colSpan={NM+1} style={{ color: 'var(--red)', fontSize: 10, padding: '6px 10px' }}>
                  ⚠ Payment % total exceeds 100% — please adjust current/30d/60d splits
                </td>
              </tr>
            )}
            <tr className="rg">
              <td>Total Cash Out</td>
              {pubData.paid.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
            </tr>
          </tbody></table></div>
        </div>
      </div>

      {/* ROYALTIES */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Royalties Payable ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            {royaltyData.map(({ game, monthly, paid, quarterly, qStart }) => (
              <React.Fragment key={game}>
                <tr className="rh"><td colSpan={NM+1}>{game}</td></tr>
                <tr className="rc">
                  <td>Royalty {quarterly ? '(Quarterly)' : '(Monthly)'}</td>
                  {monthly.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
                <tr className="ri">
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 0' }}>
                      <div className="tgl-pair">
                        <button className={`tbtn yes ${!quarterly ? 'on' : ''}`} onClick={() => setRoyQtrly(game, false)}>Monthly</button>
                        <button className={`tbtn yes ${quarterly  ? 'on' : ''}`} onClick={() => setRoyQtrly(game, true)}>Quarterly</button>
                      </div>
                      {quarterly && (
                        <select value={qStart} onChange={e => setRoyQtrStart(game, parseInt(e.target.value))}
                          style={{ fontSize: 10 }}>
                          {QUARTER_STARTS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                        </select>
                      )}
                      <select value={settings.royaltyTerms[game] ?? 1}
                        onChange={e => setRoyDefault(game, parseInt(e.target.value))}
                        style={{ fontSize: 10, padding: '3px 20px 3px 6px' }}>
                        {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </td>
                  {Array.from({ length: NM }, (_, i) => (
                    <td key={i}>
                      {monthly[i] > 0 ? (
                        <select
                          value={settings.royaltyMonthly[game]?.[i] !== undefined ? settings.royaltyMonthly[game][i] : (settings.royaltyTerms[game] ?? 1)}
                          onChange={e => setRoyMonth(game, i, parseInt(e.target.value))}
                          style={{ fontSize: 9, padding: '2px 14px 2px 4px', width: '100%' }}>
                          {DELAY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : null}
                    </td>
                  ))}
                </tr>
                <tr className="rt">
                  <td>Assumed Payment Month</td>
                  {paid.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                </tr>
              </React.Fragment>
            ))}
            {!royaltyData.length && <tr><td colSpan={NM+1} className="nomsg">No royalties calculated — set royalty rates in Standing Data.</td></tr>}
          </tbody></table></div>
        </div>
      </div>

      {/* PAYROLL */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Payroll ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            <tr className="rc">
              <td>Total Salary Cost</td>
              {payrollData.totalPeople.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
            </tr>
            <tr className="ri">
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 9, color: 'var(--t3)' }}>Current month %</span>
                  <input type="number" min="0" max="100" className="ni" style={{ width: 60 }}
                    value={settings.payrollPct}
                    onChange={e => save(s => ({ ...s, payrollPct: e.target.value }))}
                  />
                  <span style={{ fontSize: 9, color: 'var(--t3)' }}>
                    Following month: {Math.max(0, 100 - (parseFloat(settings.payrollPct)||100)).toFixed(0)}%
                  </span>
                </div>
              </td>
              {Array(NM).fill(null).map((_, i) => <td key={i} />)}
            </tr>
            <tr className="rg">
              <td>Payroll Cash Out</td>
              {payrollData.paid.map((v, i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
            </tr>
          </tbody></table></div>
        </div>
      </div>

      {/* CASHFLOW SUMMARY */}
      <div className="card">
        <div className="card-head"><span className="card-title">Cashflow Summary ({fc})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw"><table className="dt">{hdr}<tbody>
            <CollapsibleReceiptsRows platformData={platformData} summary={summary} />
            <tr className="rc">
              <td>Development Advances</td>
              {summary.devOut.map((v, i) => <td key={i}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
            </tr>
            <tr className="rc">
              <td>Publishing Costs</td>
              {summary.pubOut.map((v, i) => <td key={i}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
            </tr>
            <CollapsibleRoyaltiesRows royaltyData={royaltyData} summary={summary} />
            <tr className="rc">
              <td>Payroll</td>
              {summary.payrollOut.map((v, i) => <td key={i}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
            </tr>
            {(summary.ohCatOut || []).map(({ id, name, monthly }) => (
              <tr key={id} className="rc">
                <td>{name}</td>
                {monthly.map((v, i) => <td key={i}>{v ? <span style={{color:'var(--red)'}}>({fu(v)})</span> : '—'}</td>)}
              </tr>
            ))}
            <tr className="rx"><td colSpan={NM+1} /></tr>
            <tr className="rt">
              <td>Net Cash Movement</td>
              {summary.net.map((v, i) => <td key={i}>{colorVal(v)}</td>)}
            </tr>
            <tr className="rc">
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Opening Cash</span>
                  <FormattedInput
                    value={settings.openingCash}
                    onChange={v => save(s => ({ ...s, openingCash: v }))}
                    style={{ width: 110, textAlign: 'right', background: 'var(--s2)',
                      border: '1px solid var(--e1)', borderRadius: 'var(--r)',
                      padding: '5px 8px', fontSize: 11, color: 'var(--t1)', outline: 'none' }}
                  />
                  {saving && <span style={{ fontSize: 9, color: 'var(--t3)' }}>…</span>}
                </div>
              </td>
              {summary.open.map((v, i) => <td key={i}>{fu(v)}</td>)}
            </tr>
            <tr className="rg">
              <td>Closing Cash</td>
              {summary.close.map((v, i) => (
                <td key={i} style={{ color: v >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                  {fu(v)}
                </td>
              ))}
            </tr>
          </tbody></table></div>
        </div>
      </div>

    </div>
  );
}

// ── PAYROLL CASH SECTION DATA HELPER
export function calcPayrollCash(peopleTotal, payrollPct, NM_val) {
  const paid = Array(NM_val).fill(0);
  const pct0 = (parseFloat(payrollPct) || 100) / 100;
  const pct1 = Math.max(0, 1 - pct0);
  for (let i = 0; i < NM_val; i++) {
    const v = peopleTotal[i] || 0;
    paid[i]     += v * pct0;
    if (i+1 < NM_val) paid[i+1] += v * pct1;
  }
  return paid;
}

// Export cashflow data for use in FinStatements
export function useCashflowData(games, PS, CS, DT, functionalCurrency, convert, externalSettings, ohCategories = [], getCategoryMonthlyTotal, payrollMonthly) {
  const fc = functionalCurrency || 'USD';
  const { settings: internalSettings } = useCashSettings();
  const settings = externalSettings || internalSettings;

  return useMemo(() => {
    if (!settings || !settings.platformTerms) return { receipts: Array(NM).fill(0), devOut: Array(NM).fill(0), pubOut: Array(NM).fill(0), royOut: Array(NM).fill(0), payrollOut: Array(NM).fill(0), ohOut: Array(NM).fill(0), net: Array(NM).fill(0), open: Array(NM).fill(0), close: Array(NM).fill(0), openingCash: 0, platformBreakdown: [], royaltyBreakdown: [] };
    const receipts = Array(NM).fill(0);
    const devOut   = Array(NM).fill(0);
    const pubOut   = Array(NM).fill(0);
    const royOut   = Array(NM).fill(0);

    PLATS.forEach(p => {
      const enabled = games.filter(g => PS[g]?.[p.key]?.enabled);
      if (!enabled.length) return;
      const revUSD = Array(NM).fill(0);
      enabled.forEach(g => calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) revUSD[i] += v; }));
      const revFC = revUSD.map((v, i) => convert ? convert(v, fc, i) : v);
      const paid = applyPerMonthDelays(revFC, settings.platformTerms[p.key] ?? 1, settings.platformMonthly[p.key]);
      paid.forEach((v, i) => { receipts[i] += v; });
    });

    games.forEach(g => {
      const costs = getCostState(CS, g);
      const monthly = Array(NM).fill(0);
      ['recoup','nonRecoup'].forEach(sec => {
        costs[sec].devAdvances.forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) monthly[i] += parseFloat(v) || 0; });
      });
      const paid = applyPerMonthDelays(monthly, settings.devAdvTerms[g] ?? 0, settings.devAdvMonthly[g]);
      paid.forEach((v, i) => { devOut[i] += v; });
    });

    const total = Array(NM).fill(0);
    COST_FIELDS.filter(f => f.key !== 'devAdvances').forEach(({ key }) => {
      games.forEach(g => {
        const costs = getCostState(CS, g);
        ['recoup','nonRecoup'].forEach(sec => {
          (costs[sec][key] || []).forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) total[i] += parseFloat(v) || 0; });
        });
      });
    });
    for (let i = 0; i < NM; i++) {
      if (!total[i]) continue;
      const ov = settings.pubCostsMonthlyPct[i];
      const p0 = parseFloat(ov?.[0] ?? settings.pubCostsPct[0]) / 100 || 0;
      const p1 = parseFloat(ov?.[1] ?? settings.pubCostsPct[1]) / 100 || 0;
      const p2 = parseFloat(ov?.[2] ?? settings.pubCostsPct[2]) / 100 || 0;
      pubOut[i]     += total[i] * p0;
      if (i+1 < NM) pubOut[i+1] += total[i] * p1;
      if (i+2 < NM) pubOut[i+2] += total[i] * p2;
    }

    // Royalties — also build per-game breakdown
    const royaltyBreakdown = [];
    games.forEach(g => {
      const quarterly = settings.royaltyQuarterly[g] ?? false;
      const qStart    = settings.royaltyQuarterStart[g] ?? 0;
      const monthly   = calcGameRoyalties(PS, CS, DT, g, quarterly, qStart, settings.platformTerms, settings.platformMonthly);
      const paid      = applyPerMonthDelays(monthly, settings.royaltyTerms[g] ?? 1, settings.royaltyMonthly[g]);
      paid.forEach((v, i) => { royOut[i] += v; });
      if (paid.some(v => v > 0)) royaltyBreakdown.push({ game: g, paid });
    });

    // Platform breakdown (already computed above for receipts total — rebuild per platform)
    const platformBreakdown = [];
    PLATS.forEach(p => {
      const en = games.filter(g => PS[g]?.[p.key]?.enabled);
      if (!en.length) return;
      const rusd = Array(NM).fill(0);
      en.forEach(g => calcPlatform(p.key, PS[g][p.key]).netRaw.forEach((v, i) => { if (v !== null) rusd[i] += v; }));
      const rfc  = rusd.map((v, i) => convert ? convert(v, fc, i) : v);
      const paid = applyPerMonthDelays(rfc, settings.platformTerms[p.key] ?? 1, settings.platformMonthly[p.key]);
      if (paid.some(v => v > 0)) platformBreakdown.push({ key: p.key, label: p.label, paid });
    });

    // Overhead categories cash out
    const ohOut = Array(NM).fill(0);
    (ohCategories || []).forEach(cat => {
      const tots = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : [];
      (tots || []).forEach((v, i) => { ohOut[i] += parseFloat(v) || 0; });
    });

    // Payroll out (passed in from App.js)
    const payrollOut = Array(NM).fill(0).map((_, i) => parseFloat((payrollMonthly || [])[i]) || 0);

    const openingCash = parseFloat(settings.openingCash) || 0;
    const net = Array(NM).fill(0);
    const open = Array(NM).fill(0);
    const close = Array(NM).fill(0);
    for (let i = 0; i < NM; i++) {
      net[i]   = receipts[i] - devOut[i] - pubOut[i] - royOut[i] - payrollOut[i] - ohOut[i];
      open[i]  = i === 0 ? openingCash : close[i-1];
      close[i] = open[i] + net[i];
    }
    return { receipts, devOut, pubOut, royOut, payrollOut, ohOut, net, open, close, openingCash, platformBreakdown, royaltyBreakdown };
  }, [games, PS, CS, DT, settings, fc, convert, ohCategories, getCategoryMonthlyTotal, payrollMonthly]);
}
